/**
 * VendorMenuManager - View, add, edit, and delete official menu items for a vendor entity
 * @atomic @idempotent
 */

import { memo, useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@heroicons/react/24/outline'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { vendorAPI } from '../../services/api'
import { getInputClasses, labelClasses, smallInputClasses } from '../../styles/inputStyles'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'
import VendorMenuItemRow from './VendorMenuItemRow'

const logger = createLogger('VendorMenuManager')

const emptyItem = { name: '', price: '', description: '', offeringType: 'item', durationMinutes: '' }

const VendorMenuManager = memo(function VendorMenuManager({ entityId }) {
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()

  const [items, setItems] = useState([])
  const [loadingItems, setLoadingItems] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyItem)
  const [submitting, setSubmitting] = useState(false)
  const [nameError, setNameError] = useState('')

  useEffect(() => {
    if (!entityId) return
    setLoadingItems(true)
    vendorAPI.getVendorOfferings(entityId)
      .then(res => setItems(Array.isArray(res.data) ? res.data : []))
      .catch(err => logger.error('Failed to load menu', err))
      .finally(() => setLoadingItems(false))
  }, [entityId])

  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    if (name === 'name') setNameError('')
  }, [])

  const handleAdd = useCallback(async (e) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setNameError(t('vendor.menu.errors.nameRequired', 'Item name is required'))
      return
    }
    setSubmitting(true)
    await execute(() => vendorAPI.createVendorOffering(entityId, {
      name: form.name.trim(),
      price: form.price ? parseFloat(form.price) : undefined,
      description: form.description.trim() || undefined,
      offeringType: form.offeringType || 'item',
      durationMinutes: form.durationMinutes ? parseInt(form.durationMinutes, 10) : undefined,
    }), {
      onSuccess: (res) => {
        toast.success(t('vendor.menu.added', 'Item added'))
        setItems(prev => [...prev, res.data])
        setForm(emptyItem)
        setShowForm(false)
      },
      onError: (err) => {
        toast.error(err.message || t('vendor.menu.addError', 'Failed to add item'))
        logger.error('Create offering failed', err)
      }
    })
    setSubmitting(false)
  }, [form, entityId, execute, t])

  const handleSave = useCallback(async (id, data) => {
    await execute(() => vendorAPI.updateOffering(entityId, id, data), {
      onSuccess: () => {
        setItems(prev => prev.map(i => i.id === id ? { ...i, ...data } : i))
        toast.success(t('vendor.menu.updated', 'Item updated'))
      },
      onError: (err) => toast.error(err.message || t('vendor.menu.updateError', 'Failed to update item'))
    })
  }, [entityId, execute, t])

  const handleDelete = useCallback(async (id) => {
    await execute(() => vendorAPI.deleteOffering(entityId, id), {
      onSuccess: () => {
        setItems(prev => prev.filter(i => i.id !== id))
        toast.success(t('vendor.menu.deleted', 'Item removed'))
      },
      onError: (err) => toast.error(err.message || t('vendor.menu.deleteError', 'Failed to remove item'))
    })
  }, [entityId, execute, t])

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800">{t('vendor.menu.title', 'Menu')}</h3>
      </div>

      {loadingItems ? (
        <p className="text-xs text-gray-400 py-4 text-center">{t('common.loading', 'Loading…')}</p>
      ) : items.length === 0 && !showForm ? (
        <p className="text-xs text-gray-400 py-4 text-center">{t('vendor.menu.empty', 'No menu items yet')}</p>
      ) : (
        <ul className="space-y-2 mb-3">
          {items.map((item) => (
            <VendorMenuItemRow key={item.id} item={item} onSave={handleSave} onDelete={handleDelete} />
          ))}
        </ul>
      )}

      {showForm ? (
        <form onSubmit={handleAdd} className="border border-primary/20 rounded-xl p-3 bg-primary/5 space-y-2.5">
          <p className="text-xs font-semibold text-gray-700">{t('vendor.menu.newItem', 'New offering')}</p>
          {/* Name — full width */}
          <div>
            <label htmlFor="menu-item-name" className={labelClasses}>
              {t('vendor.menu.itemName', 'Name')} <span className="text-red-500">*</span>
            </label>
            <input id="menu-item-name" name="name" type="text"
              value={form.name} onChange={handleChange}
              className={getInputClasses(!!nameError)} maxLength={100} autoFocus />
            {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
          </div>
          {/* Type / Price / Duration in a row */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label htmlFor="menu-item-type" className={labelClasses}>{t('vendor.menu.type', 'Type')}</label>
              <select id="menu-item-type" name="offeringType" value={form.offeringType} onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 text-xs px-2 py-1.5 bg-white">
                <option value="item">{t('vendor.menu.typeItem', 'Item')}</option>
                <option value="service">{t('vendor.menu.typeService', 'Service')}</option>
                <option value="package">{t('vendor.menu.typePackage', 'Package')}</option>
              </select>
            </div>
            <div>
              <label htmlFor="menu-item-price" className={labelClasses}>{t('vendor.menu.price', 'Price')}</label>
              <input id="menu-item-price" name="price" type="text"
                value={form.price} onChange={handleChange}
                placeholder="$0.00" className={smallInputClasses} maxLength={20} />
            </div>
            <div>
              <label htmlFor="menu-item-duration" className={labelClasses}>{t('vendor.menu.durationMin', 'Min')}</label>
              <input id="menu-item-duration" name="durationMinutes" type="number" min="1" max="480"
                value={form.durationMinutes} onChange={handleChange}
                placeholder="60" className={smallInputClasses} />
            </div>
          </div>
          {/* Description */}
          <div>
            <label htmlFor="menu-item-desc" className={labelClasses}>{t('vendor.menu.description', 'Description (optional)')}</label>
            <input id="menu-item-desc" name="description" type="text"
              value={form.description} onChange={handleChange}
              className={smallInputClasses} maxLength={255} />
          </div>
          <div className="flex gap-2 pt-0.5">
            <button type="submit" disabled={submitting}
              className="flex-1 py-2 bg-primary text-white rounded-lg text-xs font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors">
              {submitting ? t('common.saving', 'Saving…') : t('vendor.menu.addItem', 'Add')}
            </button>
            <button type="button"
              onClick={() => { setShowForm(false); setForm(emptyItem); setNameError('') }}
              onTouchEnd={(e) => { e.preventDefault(); setShowForm(false); setForm(emptyItem); setNameError('') }}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-100 transition-colors">
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          onTouchEnd={(e) => { e.preventDefault(); setShowForm(true) }}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-gray-300 text-xs font-medium text-gray-500 hover:border-primary hover:text-primary transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {t('vendor.menu.add', 'Add item')}
        </button>
      )}
    </div>
  )
})

export default VendorMenuManager
